import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

const DEFAULT_TRACK = { name: '', url: '' };

@Component({
  selector: 'app-multi-track-audio-player',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="container my-4">
      @if (playlist().length > 0) {
        <!-- Vista de Metadatos del Track -->
        <div class="card shadow-sm mb-3 text-center">
          <div class="card-body">
            @if (currentFolder()) {
              <span class="mb-2 px-3 py-2 fs-6">
                {{ currentFolder() }}
              </span>
            }
            <h3 class="card-title fw-bold text-dark my-2">{{ currentSongTitle() }}</h3>
            <p class="card-text text-muted small text-break">{{ currentTrack().url }}</p>
          </div>
        </div>

        <!-- Reproductor de Audio Nativo con Soporte de Controles -->
        <div class="shadow-sm rounded overflow-hidden mb-4 p-3 bg-white text-center">
          <audio
            #audioPlayer
            class="w-100"
            controls
            [autoplay]="autoPlayEnabled()"
            [src]="currentTrack().url"
            (play)="onPlay(currentTrack().name)"
            (ended)="handleNextTrack()"
          ></audio>
          <div class="btn-group mt-2">
            <button class="btn btn-outline-secondary btn-sm" (click)="handlePrevTrack()">Anterior</button>
            <button class="btn btn-outline-secondary btn-sm" (click)="handleNextTrack()">Siguiente</button>
          </div>
        </div>

        <!-- Lista de Reproducción con Acordeón Bootstrap Adaptado -->
        <div class="text-center">
          <h4 class="fw-bold mb-3 text-uppercase text-secondary">
            HiRes Playlist {{ isTargetHostname ? 'ESFAPA' : 'FISARTH' }}
          </h4>

          <div class="accordion shadow-sm" id="playlistAccordion">
            @for (folder of groupedPlaylistKeys(); track folder; let folderIdx = $index) {
              @let tracks = groupedPlaylist()[folder];
              @let isOpen = openFolder() === folder;
              @let folderId = 'folder-collapse-' + folderIdx;

              <div class="accordion-item">
                <h5 class="m-0" [id]="'heading-' + folderIdx">
                  <button
                    class="btn btn-info w-100"
                    [class.collapsed]="!isOpen"
                    type="button"
                    (click)="toggleFolder(folder)"
                    [attr.aria-expanded]="isOpen"
                    [attr.aria-controls]="folderId"
                  >
                    <span>{{ folder }}</span> [{{ tracks.length }} {{ tracks.length === 1 ? 'pista' : 'pistas' }}]
                  </button>
                </h5>

                @if (isOpen) {
                  <div [id]="folderId" class="w-100 accordion-collapse show">
                    <div class="accordion-body bg-light p-3">
                      <div class="row g-2 justify-content-center">
                        @for (track of tracks; track track.id || track.globalIndex) {
                          @let isSelected = track.globalIndex === currentTrackIndex();
                          <div class="col-12 col-md-6 col-lg-4">
                            <div
                              (click)="handleSelectTrack(track.globalIndex)"
                              class="h-100 p-1 transition-all border"
                              [ngClass]="isSelected ? 'bg-warning text-dark fw-bold shadow-sm' : 'bg-white text-secondary'"
                              style="cursor: pointer; transition: 0.2s ease-in-out;"
                            >
                              <div class="text-left p-1 align-items-center">
                                <span [ngClass]="isSelected ? 'bg-dark text-white' : 'bg-light text-dark'" class="mb-1 p-1 rounded">
                                  #{{ track.globalIndex + 1 }}
                                </span> 
                                <span class="text-break">{{ track.songTitle }}</span>
                              </div>
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  `
})
export class MultiTrackAudioPlayerComponent implements OnInit {
  private http = inject(HttpClient);
  private apiUrl = environment.apiURL || process.env['REACT_APP_API_URL'] || '';

  playlist = signal<any[]>([]);
  currentTrackIndex = signal(0);
  autoPlayEnabled = signal(false);
  openFolder = signal<string | null>(null);

  isTargetHostname = typeof window !== 'undefined' && window.location.hostname === 'www.esfapa.edu.pe';

  ngOnInit(): void {
    this.fetchPlaylist();
  }

  private fetchPlaylist = async () => {
    try {
      this.http.post<any[]>(`${this.apiUrl}/api/users/audio`, {}).subscribe({
        next: (data) => {
          if (Array.isArray(data)) {
            this.playlist.set(data);
          }
        },
        error: (err) => console.error('Error loading playlist:', err)
      });
    } catch (err) {
      console.error('Error loading playlist:', err);
    }
  };

  groupedPlaylist = computed(() => {
    return this.playlist().reduce((groups: Record<string, any[]>, track, globalIndex) => {
      const parts = (track.name || '').split('/');
      const folderName = parts.length > 1 ? parts[0].trim() : 'Varios / Sin Carpeta';
      const songTitle = parts.length > 1 ? parts.slice(1).join('/') : track.name;

      if (!groups[folderName]) {
        groups[folderName] = [];
      }

      groups[folderName].push({
        ...track,
        songTitle,
        globalIndex,
      });

      return groups;
    }, {});
  });

  groupedPlaylistKeys = computed(() => Object.keys(this.groupedPlaylist()));

  currentTrack = computed(() => {
    const list = this.playlist();
    const idx = this.currentTrackIndex();
    return list[idx] || DEFAULT_TRACK;
  });

  currentSongParts = computed(() => (this.currentTrack().name || '').split('/'));
  currentSongTitle = computed(() => {
    const parts = this.currentSongParts();
    return parts.length > 1 ? parts.slice(1).join('/') : this.currentTrack().name;
  });
  currentFolder = computed(() => {
    const parts = this.currentSongParts();
    return parts.length > 1 ? parts[0] : null;
  });

  handleNextTrack = () => {
    const len = this.playlist().length;
    if (len === 0) return;
    this.autoPlayEnabled.set(true);
    this.currentTrackIndex.update(prev => (prev < len - 1 ? prev + 1 : 0));
    this.syncOpenFolder();
  };

  handlePrevTrack = () => {
    const len = this.playlist().length;
    if (len === 0) return;
    this.autoPlayEnabled.set(true);
    this.currentTrackIndex.update(prev => (prev > 0 ? prev - 1 : len - 1));
    this.syncOpenFolder();
  };

  handleSelectTrack = (globalIndex: number) => {
    this.autoPlayEnabled.set(true);
    this.currentTrackIndex.set(globalIndex);
    this.syncOpenFolder();
  };

  toggleFolder = (folderName: string) => {
    this.openFolder.update(prev => (prev === folderName ? null : folderName));
  };

  private syncOpenFolder() {
    const track = this.currentTrack();
    if (track?.name) {
      const parts = track.name.split('/');
      const folderName = parts.length > 1 ? parts[0].trim() : 'Varios / Sin Carpeta';
      this.openFolder.set(folderName);
    }
  }

  onPlay(trackName: string) {
    console.log(`Playing: ${trackName}`);
  }
}